import { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Chip, Divider, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { portalApplicationFormApi } from '../../api/applicationForm';
import { HEALTH_DECLARATION_CONDITIONS, HEALTH_DECLARATION_STATEMENT, StudentListItem, SubjectResult } from '../../types/student';
import { isTemporaryNumber, TEMPORARY_LABEL } from '../../components/temporaryNumber';

/** The student's whole University Application Form, read-only, laid out by
 * the form's 11 steps (2026-11-07) — every field, whoever completed it (the
 * student in the portal or their counsellor). Changes go through Change
 * requests. */

const text = (v: unknown): string | null => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
};
const date = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null);
const words = (v: string | null | undefined) => (v ? v.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : null);

const Field = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const Grid = ({ children }: { children: ReactNode }) => (
  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>{children}</Box>
);

const Section = ({ number, title, children }: { number: number; title: string; children: ReactNode }) => (
  <Paper variant="outlined" component="section" aria-label={`${number}. ${title}`} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3 }}>
    <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
      {number}. {title}
    </Typography>
    {children}
  </Paper>
);

const Results = ({ title, results }: { title: string; results: SubjectResult[] | null }) =>
  results?.length ? (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Table size="small" aria-label={title}>
        <TableHead>
          <TableRow>
            <TableCell>Subject</TableCell>
            <TableCell>Grade</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {results.map((r, i) => (
            <TableRow key={`${r.subject}-${i}`}>
              <TableCell>{r.subject}</TableCell>
              <TableCell>{r.grade ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  ) : null;

const Person = ({ title, s, prefix }: { title: string; s: StudentListItem; prefix: 'father' | 'mother' | 'guardian' }) => {
  const r = s as unknown as Record<string, unknown>;
  const name = r[`${prefix}Name`];
  if (!name && !r[`${prefix}Phone`]) return null;
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Grid>
        <Field label="Name" value={text(name)} />
        {prefix === 'guardian' && <Field label="Relationship" value={text(r.guardianRelationship)} />}
        {prefix === 'guardian' && <Field label="Passport / NIC" value={text(r.guardianPassportOrNic)} />}
        <Field label="Phone" value={text(r[`${prefix}Phone`])} />
        <Field label="WhatsApp" value={text(r[`${prefix}Whatsapp`])} />
        <Field label="Email" value={text(r[`${prefix}Email`])} />
        {prefix !== 'guardian' && <Field label="Occupation" value={text(r[`${prefix}Occupation`])} />}
        {prefix !== 'guardian' && <Field label="Organisation" value={text(r[`${prefix}Organization`])} />}
        {prefix !== 'guardian' && <Field label="Office phone" value={text(r[`${prefix}OfficePhone`])} />}
        <Field label="Address" value={text(r[`${prefix}Address`])} />
        <Field label="Sponsor" value={text(r[`${prefix}IsSponsor`])} />
      </Grid>
    </Box>
  );
};

export const PortalApplicationView = ({ student: s }: { student: StudentListItem }) => {
  const qualifications = useQuery({ queryKey: ['portal-application-qualifications'], queryFn: () => portalApplicationFormApi.listQualifications(s.id) });
  const answers = s.healthDeclarationAnswers ?? [];

  return (
    <Stack spacing={2}>
      <Section number={1} title="Application Details">
        <Grid>
          <Field label={isTemporaryNumber(s.applicationNumber) ? TEMPORARY_LABEL.application : 'Application number'} value={s.applicationNumber} />
          <Field label="Branch" value={s.branch?.name ?? null} />
          <Field label="University" value={s.preferredUniversity?.name ?? null} />
          <Field label="Programme" value={s.preferredProgram?.name ?? null} />
          <Field label="Semester / Intake" value={s.preferredIntakeLabel ?? s.preferredIntake?.name ?? null} />
          <Field label="Academic year" value={text(s.academicYear)} />
        </Grid>
      </Section>

      <Section number={2} title="Personal Details">
        <Grid>
          <Field label="Name" value={`${s.firstName} ${s.lastName}`} />
          <Field label="Other names" value={text(s.otherNames)} />
          <Field label="Preferred name" value={text(s.preferredName)} />
          <Field label="Gender" value={words(s.gender)} />
          <Field label="Date of birth" value={date(s.dateOfBirth)} />
          <Field label="Marital status" value={words(s.maritalStatus)} />
          <Field label="Nationality" value={s.nationalityCountry?.name ?? null} />
          <Field label="Country of birth" value={s.countryOfBirth?.name ?? null} />
          <Field label="NIC" value={text(s.nicNumber)} />
          <Field label="Passport number" value={text(s.passportNumber)} />
          <Field label="Passport issued" value={date(s.passportIssueDate)} />
          <Field label="Passport expires" value={date(s.passportExpiryDate)} />
          <Field label="Mobile" value={text(s.phone)} />
          <Field label="WhatsApp" value={text(s.whatsappNumber)} />
          <Field label="Telephone" value={text(s.telephone)} />
          <Field label="Email" value={text(s.email)} />
          <Field label="Address" value={[s.address, s.city, s.province, s.postalCode].filter(Boolean).join(', ') || null} />
          <Field label="Emergency contact" value={[s.emergencyContactName, s.emergencyContactRelationship, s.emergencyContactPhone].filter(Boolean).join(' · ') || null} />
        </Grid>
      </Section>

      <Section number={3} title="Academic Details">
        <Stack spacing={2}>
          <Grid>
            <Field label="School" value={text(s.schoolName)} />
            <Field label="O/L" value={[words(s.olType), s.olYear].filter(Boolean).join(' · ') || null} />
            <Field label="A/L" value={[words(s.alType), s.alYear, words(s.alStream)].filter(Boolean).join(' · ') || null} />
            <Field label="Overall GPA" value={text(s.overallGpa)} />
          </Grid>
          <Results title="O/L results" results={s.olResults} />
          <Results title="A/L results" results={s.alResults} />
        </Stack>
      </Section>

      <Section number={4} title="Higher Education">
        {qualifications.data?.length ? (
          <Stack divider={<Divider flexItem />} spacing={1}>
            {qualifications.data.map((q) => (
              <Box key={q.id}>
                <Typography variant="body2" fontWeight={600}>
                  {q.name} — {q.institution}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[q.academicLevel?.name, q.majorSubject, q.countryRef?.name ?? q.country, q.gpa ? `GPA ${q.gpa}` : null, words(q.completionStatus), [date(q.fromDate), date(q.toDate)].filter(Boolean).join(' – ')]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No higher education recorded.
          </Typography>
        )}
      </Section>

      <Section number={5} title="Preferred Study">
        <Grid>
          <Field label="Qualification level" value={s.preferredQualificationLevel?.name ?? null} />
          <Field label="Preferred city" value={text(s.preferredCity)} />
          <Field label="Alternative country" value={s.alternativeCountry?.name ?? null} />
          <Field label="Alternative university" value={s.alternativeUniversity?.name ?? null} />
          <Field label="Budget" value={s.budget ? `${s.budgetCurrency?.code ?? ''} ${s.budget}`.trim() : null} />
          <Field label="Scholarship required" value={text(s.scholarshipRequired)} />
          <Field label="English test" value={[s.englishTestType, s.englishTestScore, date(s.englishTestDate)].filter(Boolean).join(' · ') || null} />
        </Grid>
      </Section>

      <Section number={6} title="Parents / Guardian">
        <Stack spacing={2} divider={<Divider flexItem />}>
          <Person title="Father" s={s} prefix="father" />
          <Person title="Mother" s={s} prefix="mother" />
          <Person title="Guardian" s={s} prefix="guardian" />
        </Stack>
      </Section>

      <Section number={7} title="Visa History">
        <Grid>
          <Field label="Applied for a visa before" value={text(s.visaAppliedBefore)} />
          <Field label="Visa type" value={words(s.visaType)} />
          <Field label="Refused" value={text(s.visaRefused)} />
          <Field label="Refused by" value={s.visaRefusedCountry?.name ?? null} />
          <Field label="Refusal reason" value={text(s.visaRefusalReason)} />
          <Field label="Comments" value={text(s.visaComments)} />
          <Field label="Travel history" value={text(s.travelHistory)} />
        </Grid>
      </Section>

      <Section number={8} title="Financial Information">
        <Grid>
          <Field label="Funding method" value={words(s.fundingMethod)} />
          <Field label="Monthly income" value={text(s.monthlyIncome)} />
          <Field label="Annual income" value={text(s.annualIncome)} />
          <Field label="Sponsor details" value={text(s.sponsorDetails)} />
        </Grid>
      </Section>

      <Section number={9} title="Documents">
        <Typography variant="body2" color="text.secondary">
          Your documents and their review status are on the Documents tab.
        </Typography>
      </Section>

      <Section number={10} title="Declaration">
        <Stack spacing={1.5}>
          {/* The health declaration's opening statement (2026-11-04), word for
              word as on the form, above the declaration and the conditions. */}
          {s.healthDeclarationRequired && (
            <Typography
              variant="body1"
              data-testid="health-declaration-statement"
              sx={{
                fontWeight: 700,
                px: 1.5,
                py: 1.25,
                borderLeft: 4,
                borderColor: 'primary.main',
                borderRadius: 1,
                bgcolor: 'action.hover',
                overflowWrap: 'anywhere',
              }}
            >
              {HEALTH_DECLARATION_STATEMENT}
            </Typography>
          )}
          <Grid>
            <Field label="Student agreement accepted" value={text(s.studentAgreementAccepted)} />
            <Field label="Privacy consent" value={text(s.privacyConsentAccepted)} />
            <Field label="Marketing consent" value={text(s.marketingConsentAccepted)} />
            <Field label="Student signature" value={[s.studentSignatureName, date(s.studentSignatureAt)].filter(Boolean).join(' · ') || null} />
            <Field label="Guardian signature" value={[s.guardianSignatureName, date(s.guardianSignatureAt)].filter(Boolean).join(' · ') || null} />
            <Field label="Health declaration" value={s.healthDeclarationRequired ? 'Included' : 'Not included'} />
          </Grid>
          {s.healthDeclarationRequired && (
            <Table size="small" aria-label="Health declaration">
              <TableBody>
                {HEALTH_DECLARATION_CONDITIONS.map((c) => {
                  const a = answers.find((x) => x.key === c.key);
                  return (
                    <TableRow key={c.key}>
                      <TableCell>{c.label}</TableCell>
                      <TableCell>{a?.answer ? <Chip size="small" label={a.answer === 'YES' ? 'Yes' : 'No'} /> : '—'}</TableCell>
                      <TableCell>{a?.statement ?? ''}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Stack>
      </Section>

      <Section number={11} title="Review">
        <Typography variant="body2" color="text.secondary">
          This is your application as it is on file at IMC. To change anything, use Change requests — your counsellor reviews every change.
        </Typography>
      </Section>
    </Stack>
  );
};
